<template>
    <div class="hts-form-button">
        <button
            :type="cType"
            class="btn"
            :class="cClass"
            :readonly="readonly"
            :disabled="disabled"
            @click="handleClick">
            <div
                class="relative flex items-center"
                :class="{ 'gap-2': hasIcon }">
                <span
                    v-if="loading"
                    class="loading loading-spinner" />
                <slot
                    v-if="!loading && iconPosition !== 'right'"
                    name="icon">
                    <hts-icons
                        :icon-name="iconName"
                        :size="iconSize || size"
                        :style="iconStyle"
                        :class="iconClass" />
                </slot>
                <div v-if="type !== 'icon'">
                    <slot class="leading-loose" />
                </div>
                <slot
                    v-if="!loading && iconPosition === 'right'"
                    name="icon">
                    <hts-icons
                        :icon-name="iconName"
                        :size="iconSize || size"
                        :style="iconStyle"
                        :class="iconClass" />
                </slot>
            </div>
        </button>
    </div>
</template>

<script lang="ts" setup>
import { daisyuiColor } from '~/components/utils/daisyui'

type InputType = 'button' | 'reset' | 'submit'
const props = defineProps({
  type: {
    type: String,
    default: 'button'
  },
  glass: Boolean,
  size: {
    type: String,
    default: ''
  },
  color: {
    type: String,
    default: ''
  },
  loading: Boolean,
  icon: String,
  svg: String,
  iconName: String,
  iconSize: String,
  iconClass: String,
  iconStyle: String,
  wide: Boolean,
  outline: {
    type: Boolean as PropType<boolean>,
    default: true
  },
  readonly: Boolean,
  disabled: Boolean,
  iconPosition: {
    type: String,
    default: ''
  },
  textTransform: {
    type: String,
    default: ''
  }
})
const emits = defineEmits(['click', 'submit'])
const { type, icon, svg, iconClass, iconName, loading, color, size, wide, outline, iconStyle, glass, textTransform, readonly, disabled } = toRefs(props)
const cType = computed<InputType>(() => {
  if (['button', 'reset', 'submit'].includes(type.value)) {
    return type.value as InputType
  }
  return 'button' as InputType
})
const hasIcon = computed<boolean>(() => {
  return !!(icon?.value || svg?.value || iconName?.value)
})
const cClass = computed(() => {
  return {
    ...cSize.value,
    ...cColor,
    ...{
      'btn-square': ['square'].includes(type.value),
      'btn-circle': ['circle', 'icon'].includes(type.value),
      'btn-icon': type.value === 'icon',
      'btn-transparent': type.value === 'icon',
      glass: glass.value,
      'btn-wide': wide.value,
      'btn-outline': outline.value
    },
    ...textTransformClass.value
  }
})
const cColor = daisyuiColor('btn', color?.value)
const cSize = computed(() => {
  return {
    'btn-xs': ['v-mini', 'mini'].includes(size.value),
    'btn-sm': size.value === 'small',
    'btn-lg': ['large', 'xl', '2xl', '3xl'].includes(size.value)
  }
})
const textTransformClass = computed(() => {
  return {
    lowercase: textTransform.value === 'lowercase',
    capitalize: textTransform.value === 'capitalize',
    normalCase: textTransform.value === 'normal-case'
  }
})
const cIconStyle = computed<string>(() => iconStyle?.value as string)

/** Methods */
const handleClick = (e: any) => {
  if (e instanceof MouseEvent) {
    if (type.value !== 'submit') {
      e.preventDefault()
    }
    e.stopPropagation()
  }
  if (loading.value) {
    return
  }
  emits('click', e)
}
</script>
